/**
 * Custom Gallery Navigation
 * Controls hover-images gallery with arrow navigation
 * ONLY WORKS ON MOBILE/TOUCH DEVICES
 */

class CustomGalleryNavigation {
  constructor(container) {
    this.container = container;
    this.slides = container.querySelectorAll('.product-item__bg__slide');
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.isTouchDevice = this.detectTouchDevice();
    
    // Only initialize on touch devices
    if (this.isTouchDevice) {
      this.init();
    } else {
    
    }
  }
  
  detectTouchDevice() {
    return (('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0) ||
            (navigator.msMaxTouchPoints > 0) ||
            window.matchMedia('(pointer: coarse)').matches);
  }
  
  init() {
    if (this.totalSlides <= 1) return;
    
    // Read current state from CSS variables if they exist
    this.readCurrentState();
    
    // Set initial CSS variables
    this.updateCSSVariables();
    
    // Create navigation arrows for mobile devices only
    this.createArrows();
    
    // Bind touch events and arrow events
    this.bindTouchEvents();
    this.bindArrowEvents();
    
    // Initial slide update to match current state
    this.updateSlide();
  }
  
  readCurrentState() {
    // Get computed styles to read current CSS variables
    const computedStyle = window.getComputedStyle(this.container);
    
    // Read current slider index from CSS variable
    const currentSliderIndex = computedStyle.getPropertyValue('--slider-index').trim();
    const currentSlidesCount = computedStyle.getPropertyValue('--slides-count').trim();
    
    // Parse and validate the values
    if (currentSliderIndex && !isNaN(parseInt(currentSliderIndex))) {
      this.currentIndex = parseInt(currentSliderIndex);
      
      // Ensure the index is within valid range
      if (this.currentIndex < 0) {
        this.currentIndex = 0;
      } else if (this.currentIndex >= this.totalSlides) {
        this.currentIndex = this.totalSlides - 1;
      }
    }
    
    // Validate slides count matches
    if (currentSlidesCount && parseInt(currentSlidesCount) !== this.totalSlides) {
      // If slides count doesn't match, reset to first slide
      this.currentIndex = 0;
    }
    
   
  }
  
  updateCSSVariables() {
    // Update CSS custom properties for ::after positioning
    this.container.style.setProperty('--slides-count', this.totalSlides);
    this.container.style.setProperty('--slider-index', this.currentIndex);
  }
  
  createArrows() {
    // Create left arrow
    this.leftArrow = document.createElement('button');
    this.leftArrow.className = 'custom-gallery-nav custom-gallery-nav--prev';
    this.leftArrow.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    this.leftArrow.setAttribute('aria-label', 'Imagen anterior');
    
    // Create right arrow
    this.rightArrow = document.createElement('button');
    this.rightArrow.className = 'custom-gallery-nav custom-gallery-nav--next';
    this.rightArrow.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    this.rightArrow.setAttribute('aria-label', 'Siguiente imagen');
    
    // Append arrows to container
    this.container.appendChild(this.leftArrow);
    this.container.appendChild(this.rightArrow);
    
    // Update arrow states
    this.updateArrowStates();
  }
  
  bindArrowEvents() {
    // Arrow events for mobile devices
    if (!this.leftArrow || !this.rightArrow) return;
    
    this.leftArrow.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.goToPreviousTouch();
    });
    
    this.rightArrow.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.goToNextTouch();
    });
  }
  
  bindTouchEvents() {
    // Listen to scroll events to sync the progress bar
    const slider = this.container.querySelector('.product-item__bg__slider');
    if (!slider) return;
    
    let scrollTimeout;
    slider.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.syncFromScroll(slider);
      }, 50); // Debounce scroll events
    });
    
    // Also listen for external CSS changes
    this.observeCSSChanges();
  }
  
  syncFromScroll(slider) {
    // Calculate current index based on scroll position
    const scrollLeft = slider.scrollLeft;
    const slideWidth = slider.clientWidth;
    const newIndex = Math.round(scrollLeft / slideWidth);
    
    if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.totalSlides) {
      this.currentIndex = newIndex;
      this.updateCSSVariables();
      
      // Update arrow states
      this.updateArrowStates();
      
      // Trigger custom event
      this.container.dispatchEvent(new CustomEvent('slideChange', {
        detail: {
          currentIndex: this.currentIndex,
          totalSlides: this.totalSlides
        }
      }));
    }
  }
  
  observeCSSChanges() {
    // Create a MutationObserver to watch for style changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          // Re-read the current state when styles change externally
          this.readCurrentState();
        }
      });
    });
    
    // Start observing
    observer.observe(this.container, {
      attributes: true,
      attributeFilter: ['style']
    });
  }
  
  goToPreviousTouch() {
    // For touch devices, scroll to previous slide
    const slider = this.container.querySelector('.product-item__bg__slider');
    if (!slider) return;
    
    const slideWidth = slider.clientWidth;
    const targetIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.totalSlides - 1;
    const targetScrollLeft = targetIndex * slideWidth;
    
    slider.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });
  }
  
  goToNextTouch() {
    // For touch devices, scroll to next slide
    const slider = this.container.querySelector('.product-item__bg__slider');
    if (!slider) return;
    
    const slideWidth = slider.clientWidth;
    const targetIndex = this.currentIndex < this.totalSlides - 1 ? this.currentIndex + 1 : 0;
    const targetScrollLeft = targetIndex * slideWidth;
    
    slider.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });
  }
  
  updateSlide() {
    // On mobile, don't hide slides - let native scroll work
    // Just update CSS variables for ::after bar position
    this.updateCSSVariables();
    
    // Update arrow states
    this.updateArrowStates();
    
    // Trigger custom event
    this.container.dispatchEvent(new CustomEvent('slideChange', {
      detail: {
        currentIndex: this.currentIndex,
        totalSlides: this.totalSlides
      }
    }));
  }
  
  updateArrowStates() {
    if (!this.leftArrow || !this.rightArrow) return;
    
    // Add active states (optional: can disable at boundaries)
    this.leftArrow.classList.toggle('is-disabled', this.currentIndex === 0);
    this.rightArrow.classList.toggle('is-disabled', this.currentIndex === this.totalSlides - 1);
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Wait a bit for other scripts to set initial states
  setTimeout(() => {
    // Find only mobile hover-images containers
    const galleries = document.querySelectorAll('hover-images.mobile-only');
    
    galleries.forEach(gallery => {
      // Only initialize if it has multiple slides
      const slides = gallery.querySelectorAll('.product-item__bg__slide');
      if (slides.length > 1) {
        new CustomGalleryNavigation(gallery);
      }
    });
  }, 100); // Small delay to ensure other scripts have run
});

// Export for manual initialization
window.CustomGalleryNavigation = CustomGalleryNavigation; 